import { Target, RotateCcw, PenTool, HelpCircle } from 'lucide-react'

const STATUS_LABELS = {
  not_started: 'Not started',
  developing: 'Developing',
  competent: 'Competent',
  strong: 'Strong',
  needs_review: 'Needs review',
}

function statusClass(status) {
  if (status === 'strong') return 'text-emerald-400'
  if (status === 'competent') return 'text-cinema-glow'
  if (status === 'needs_review') return 'text-amber-400'
  return 'text-cinema-gold'
}

function ConfidenceBar({ value }) {
  const percent = Math.max(0, Math.min(100, Number(value) || 0))
  const filled = Math.round(percent / 10)
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-xs tracking-widest text-cinema-gold/80" aria-hidden="true">
        {'█'.repeat(filled)}{'░'.repeat(10 - filled)}
      </span>
      <span className="font-mono text-xs text-cinema-muted">{percent}%</span>
    </div>
  )
}

function actionIcon(action) {
  if (action === 'practice') return PenTool
  if (action === 'check') return HelpCircle
  return RotateCcw
}

export default function AITutorLearningProfile({
  profile,
  compact = false,
  onPractice,
  disabled = false,
}) {
  const topics = profile?.topics || []
  const recommendations = profile?.recommendations || []
  const recommendedReview = profile?.recommended_review || []
  const current = profile?.currentTopic
  const courseTitle = typeof profile?.course === 'string'
    ? profile.course
    : profile?.course?.title || profile?.courseMeta?.title

  if (compact) {
    const practiceItems = recommendations.slice(0, 2)
    return (
      <div className="cinema-glass border border-white/8 p-3 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <span className="cinema-label text-xs">Your Learning</span>
          {courseTitle && (
            <span className="text-xs text-cinema-muted truncate max-w-[55%]">{courseTitle}</span>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-cinema-muted mb-1 font-medium">Current topic</p>
          {current?.name ? (
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm text-white font-medium truncate">{current.name}</p>
              <span className={`text-xs font-medium ${statusClass(current.status)}`}>
                {STATUS_LABELS[current.status] || current.status}
              </span>
            </div>
          ) : (
            <p className="text-xs text-cinema-muted">
              Your learning profile will appear as you study with the tutor.
            </p>
          )}
        </div>

        {practiceItems.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-cinema-muted mb-1.5 font-medium">Recommended practice</p>
            <div className="flex flex-wrap gap-1.5">
              {practiceItems.map((item) => (
                <button
                  key={`${item.topicKey || item.topic}-${item.action}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => onPractice && onPractice(item)}
                  className="px-2.5 py-1 text-xs cinema-glass border border-white/10 text-cinema-soft hover:text-white hover:border-cinema-glow/40 disabled:opacity-40"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="cinema-glass-strong border border-white/10 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Target size={14} className="text-cinema-gold" />
        <h3 className="font-display text-sm sm:text-base text-white">Your Progress</h3>
      </div>

      {topics.length === 0 ? (
        <p className="text-xs sm:text-sm text-cinema-muted">
          As you ask questions and check your understanding, topic progress will appear here.
        </p>
      ) : (
        <div className="space-y-3">
          {topics.slice(0, 8).map((topic) => (
            <div key={topic.topicKey || topic.topic} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs sm:text-sm text-white truncate font-medium">{topic.topic}</span>
                <span className={`text-xs ${statusClass(topic.status)} font-medium`}>
                  {STATUS_LABELS[topic.status] || topic.status}
                </span>
              </div>
              <ConfidenceBar value={topic.confidence} />
            </div>
          ))}
        </div>
      )}

      {(recommendations.length > 0 || recommendedReview.length > 0) && (
        <div className="pt-2 border-t border-white/10 space-y-2">
          <p className="text-xs uppercase tracking-wider text-cinema-muted font-medium">Recommended</p>
          <div className="flex flex-wrap gap-1.5">
            {recommendations.slice(0, 4).map((item) => {
              const Icon = actionIcon(item.action)
              return (
                <button
                  key={`${item.topicKey || item.topic}-${item.action}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => onPractice && onPractice(item)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs cinema-glass border border-white/10 text-cinema-soft hover:text-white hover:border-cinema-glow/40 disabled:opacity-40"
                >
                  <Icon size={12} />
                  {item.label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {(profile?.recentAssessments || []).length > 0 && (
        <div className="pt-2 border-t border-white/10 space-y-2">
          <p className="text-xs uppercase tracking-wider text-cinema-muted font-medium">Recent checks</p>
          <ul className="space-y-1.5">
            {profile.recentAssessments.slice(0, 3).map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-2 text-xs sm:text-sm">
                <span className="text-cinema-soft truncate">{item.topic}</span>
                <span className="font-mono text-cinema-gold flex-shrink-0 font-medium">{item.score}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {profile?.nextRecommendation?.label && (
        <p className="text-xs sm:text-sm text-cinema-muted">
          Next: {profile.nextRecommendation.label}
        </p>
      )}
    </div>
  )
}
